import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { generateId } from "../../utils/generateId";
import { RootState } from "../rootReducer";
import { MealSlot, PlannedMeal } from "../../models/meal-plan";
import { makeSyncUpsert, makeSyncRemove } from "../syncUpsert";

export const mealPlanAdapter = createEntityAdapter<PlannedMeal>();
type MealPlanState = EntityState<PlannedMeal, string>;
const initialState: MealPlanState = mealPlanAdapter.getInitialState();

type AddPlannedMealPayload = {
  id?: string;
  recipeId: string;
  mealDate: string; // "YYYY-MM-DD"
  slot: MealSlot;
  sortOrder?: number; // defaults to the end of that day+slot
};

export const mealPlanSlice = createSlice({
  name: "mealPlan",
  initialState,
  reducers: {
    upsertManyFromSync: makeSyncUpsert(mealPlanAdapter),
    removeManyFromSync: makeSyncRemove(mealPlanAdapter),

    addPlannedMeal(state: MealPlanState, action: PayloadAction<AddPlannedMealPayload>): void {
      const { recipeId, mealDate, slot } = action.payload;
      const id = action.payload.id ?? generateId();

      // Default sortOrder = end of the day+slot (max existing + 1).
      let sortOrder = action.payload.sortOrder;
      if (sortOrder === undefined) {
        const siblings = Object.values(state.entities).filter(
          (m): m is PlannedMeal =>
            !!m && !m.deletedAt && m.mealDate === mealDate && m.slot === slot,
        );
        sortOrder = siblings.reduce((max, m) => Math.max(max, m.sortOrder), -1) + 1;
      }

      mealPlanAdapter.addOne(state, { id, recipeId, mealDate, slot, sortOrder });
    },

    // Move a planned meal to another day/slot and/or reorder it.
    movePlannedMeal(
      state: MealPlanState,
      action: PayloadAction<{ id: string; mealDate?: string; slot?: MealSlot; sortOrder?: number }>,
    ): void {
      const { id, mealDate, slot, sortOrder } = action.payload;
      mealPlanAdapter.updateOne(state, {
        id,
        changes: {
          ...(mealDate !== undefined && { mealDate }),
          ...(slot !== undefined && { slot }),
          ...(sortOrder !== undefined && { sortOrder }),
        },
      });
    },

    // Soft-delete (dirty-tracked → pushed as a tombstone on the next sync).
    removePlannedMeal(state: MealPlanState, action: PayloadAction<string>): void {
      mealPlanAdapter.updateOne(state, {
        id: action.payload,
        changes: { deletedAt: new Date().toISOString() },
      });
    },
  },
});

const mealPlanSelectors = mealPlanAdapter.getSelectors((s: RootState) => s.mealPlan);

export const selectPlannedMealById = mealPlanSelectors.selectById;

// Hides soft-deleted rows; sync reads entities directly via the adapter.
export const selectAllPlannedMeals = createSelector(
  mealPlanSelectors.selectAll,
  (meals) => meals.filter((m) => !m.deletedAt),
);

// Planned meals within an inclusive "YYYY-MM-DD" range (string compare = chronological).
export const selectPlannedMealsInRange = (s: RootState, start: string, end: string) =>
  selectAllPlannedMeals(s).filter((m) => m.mealDate >= start && m.mealDate <= end);

export const {
  addPlannedMeal,
  movePlannedMeal,
  removePlannedMeal,
  upsertManyFromSync: upsertMealPlanFromSync,
  removeManyFromSync: removeMealPlanFromSync,
} = mealPlanSlice.actions;

export default mealPlanSlice.reducer;
